import React from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, Button } from '@mui/material';

import {
  Modal,
  ModalButton,
  ModalContents,
  ModalTitle,
  ModalTypography,
} from 'components/Modal/Modal';
import config from 'config';
import ApplicationTabledIcon from 'assets/modalIcons/application-tabled-icon.avif';
import ApplicationAcceptedIcon from 'assets/modalIcons/application-accepted-icon.avif';

import { selectEmailVerificationRequested } from './selectors';
import { duplicateEinEmail } from './duplicateEinEmail';

export const PostSubmissionModal = ({
  submitDone,
  errorCode,
  confirmDuplicateEin,
  reset,
  isClaimed,
  duplicateNonprofitId,
  duplicateNonprofitName,
  formValues,
  selectNonprofit,
}: {
  submitDone: boolean;
  errorCode: string;
  reset: () => void;
  confirmDuplicateEin: () => void;
  selectNonprofit: (nonprofitId: string) => void;
  isClaimed: boolean;
  duplicateNonprofitId: string;
  duplicateNonprofitName: string;
  formValues: any;
}) => {
  const emailVerificationRequested = useSelector(selectEmailVerificationRequested);
  if (errorCode) {
    const errorContents = {
      OrganizationExists: (
        <>
          <ModalTitle>Organization Already Registered</ModalTitle>
          <ModalTypography>
            We&apos;ve identified an active organization in our system with this EIN. You can claim
            or request access to it by returning to the &lsquo;Claim Your Organization&rsquo; page
            using the link below.
          </ModalTypography>
        </>
      ),
      InvisibleOrganizationExists: (
        <>
          <ModalTitle>Organization Already Exists</ModalTitle>
          <ModalTypography>
            We’ve found an organization with this EIN in our system, but it’s not currently visible.
            To claim or update this organization, please contact our Nonprofit Success Team for
            assistance.
          </ModalTypography>
        </>
      ),
      GovOrganizationExists: (
        <>
          <ModalTitle>Government Entity Warning</ModalTitle>
          <ModalTypography>
            A government entity with this EIN is already registered in our system. You can claim or
            request access to the existing profile by clicking on the organization name below:
          </ModalTypography>
          <ModalTypography>
            <Button
              variant="text"
              onClick={() => {
                reset();
                selectNonprofit(duplicateNonprofitId);
              }}
            >
              {duplicateNonprofitName}
            </Button>
          </ModalTypography>
          <ModalTypography>
            If you believe this is a different entity, you may proceed with creating a new profile.
            Please verify the information carefully to avoid duplication.
          </ModalTypography>
        </>
      ),
      InvisibleGovOrganizationExists: (
        <>
          <ModalTitle>Government Entity Already Exists</ModalTitle>
          <ModalTypography>
            This government entity with the EIN you provided already exists in our database but
            isn’t visible. Please contact our Nonprofit Support (NPS) team for further guidance.
          </ModalTypography>
        </>
      ),
      AlreadySignedUp: (
        <>
          <ModalTitle>You&apos;re already on the platform!</ModalTitle>
          <ModalTypography>
            This email address has already been claimed. Sign in now or reset your password.
          </ModalTypography>
          <ModalTypography>
            Still having trouble? Reach out to our{' '}
            <a
              href={`mailto:${config.nonprofitSuccessTeamEmail}?subject=My email address is already claimed`}
            >
              Nonprofit Success Team
            </a>
            !
          </ModalTypography>
        </>
      ),
      NonprofitAlreadyClaimed: (
        <>
          <ModalTitle>This organization has already been claimed</ModalTitle>
          <ModalTypography>
            This nonprofit profile has already been claimed. Was that by you? Sign in now with your
            email and password to start managing your volunteering and donations.
          </ModalTypography>
          <ModalTypography>
            Still having trouble? Reach out to our{' '}
            <a
              href={`mailto:${config.nonprofitSuccessTeamEmail}?subject=My organization is already claimed`}
            >
              Nonprofit Success Team
            </a>
            !
          </ModalTypography>
        </>
      ),
    };
    const errorCtas = {
      OrganizationExists: (
        <ModalButton
          onClick={() => {
            reset();
            selectNonprofit(duplicateNonprofitId);
          }}
        >
          Go to Claim Page
        </ModalButton>
      ),
      InvisibleOrganizationExists: (
        <ModalButton
          onClick={() => {
            duplicateEinEmail(formValues);
          }}
        >
          Contact Nonprofit Success Team
        </ModalButton>
      ),
      GovOrganizationExists: (
        <ModalButton onClick={confirmDuplicateEin}>
          Proceed with Profile Creation for ‘{formValues.nonprofitName}’
        </ModalButton>
      ),
      InvisibleGovOrganizationExists: (
        <ModalButton
          onClick={() => {
            duplicateEinEmail(formValues);
          }}
        >
          Contact Nonprofit Success Team
        </ModalButton>
      ),
      AlreadySignedUp: (
        <>
          <Link to="/forgot" style={{ marginRight: 15 }}>
            <ModalButton onClick={reset}>Reset password</ModalButton>
          </Link>
          <Link to="/login">
            <ModalButton onClick={reset}>Sign in</ModalButton>
          </Link>
        </>
      ),
      NonprofitAlreadyClaimed: (
        <Link to="/login">
          <ModalButton onClick={reset}>Sign in</ModalButton>
        </Link>
      ),
    };

    const errorContent = errorContents[errorCode] || (
      <>
        <ModalTitle>Oh no!</ModalTitle>
        <ModalTypography sx={{ mb: 2 }}>
          Looks like something isn&apos;t quite right there. If you were invited to the platform,
          you might already have an account with this email.
        </ModalTypography>
        <ModalTypography sx={{ mb: 2 }}>
          Double check you don&apos;t have an email from Deed and log in{' '}
          <Link to="/login">here</Link>. Alternatively, try{' '}
          <Link to="/forgot">resetting your password</Link>.
        </ModalTypography>
        <ModalTypography>
          Still need help? Reach out to
          <br />
          <a href={`mailto:${config.nonprofitSuccessTeamEmail}`}>
            {config.nonprofitSuccessTeamEmail}
          </a>
          !
        </ModalTypography>
      </>
    );
    const errorCta = errorCtas[errorCode] || (
      <Box style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
        <Link to="/login">
          <ModalButton>Login</ModalButton>
        </Link>
        <Link to="/forgot">
          <ModalButton>Password Reset</ModalButton>
        </Link>
      </Box>
    );

    return (
      <Modal open={submitDone}>
        <ModalContents
          header={<img src={ApplicationTabledIcon} width={308} alt="application tabled icon" />}
          body={errorContent}
          actions={errorCta}
        />
      </Modal>
    );
  }

  return (
    <Modal open={submitDone}>
      <ModalContents
        header={<img src={ApplicationAcceptedIcon} width={347} alt="application accepted icon" />}
        body={
          <>
            <ModalTitle>Thank you for your submission!</ModalTitle>
            <ModalTypography>
              {isClaimed
                ? "We've forwarded your request to access this nonprofit to the nonprofit admin, and we will keep you informed once your request has been approved."
                : "Congrats! You're one step closer to becoming a registered organization with Deed."}
              <br />
              {emailVerificationRequested ? (
                <>Please keep an eye on your inbox for the email verification.</>
              ) : (
                <>Please keep an eye on your email for more from our Nonprofit Success Team.</>
              )}
            </ModalTypography>
            <ModalTypography>
              Have more questions? Reach out to{' '}
              <a
                href={`mailto:${config.nonprofitSuccessTeamEmail}?subject=I'm trying to request Access from a Claimed Organization`}
              >
                {config.nonprofitSuccessTeamEmail}
              </a>
              !
            </ModalTypography>
          </>
        }
        actions={
          <Link to="/login">
            <ModalButton onClick={reset}>Okay</ModalButton>
          </Link>
        }
      />
    </Modal>
  );
};
